import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { MagnifyingGlass, Funnel } from '@phosphor-icons/react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function MarketplacePage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [masterclasses, setMasterclasses] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState(searchParams.get('category') || 'all');
  const [selectedFormat, setSelectedFormat] = useState('all');

  useEffect(() => {
    fetchData();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const cat = searchParams.get('category');
    if (cat) {
      setSelectedCategory(cat);
    }
  }, [searchParams]);

  const fetchData = async () => {
    try {
      const [categoriesRes, classesRes] = await Promise.all([
        axios.get(`${API}/categories`),
        axios.get(`${API}/masterclasses?approved=true`)
      ]);
      setCategories(categoriesRes.data.categories);
      setMasterclasses(classesRes.data);
    } catch {
      // silently handle fetch errors
    } finally {
      setLoading(false);
    }
  };

  const handleAISearch = async () => {
    if (!searchQuery.trim()) return;
    try {
      setLoading(true);
      const response = await axios.post(`${API}/ai/search`, { query: searchQuery });
      setMasterclasses(response.data);
    } catch {
      // silently handle search errors
    } finally {
      setLoading(false);
    }
  };

  const filteredClasses = masterclasses.filter(mc => {
    if (selectedCategory !== 'all' && mc.category !== selectedCategory) return false;
    if (selectedFormat !== 'all' && mc.format !== selectedFormat) return false;
    return true;
  });

  return (
    <div className="min-h-screen bg-white text-gray-900">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 gradient-header">
        <div className="max-w-7xl mx-auto px-6 py-4 flex justify-between items-center">
          <div
            onClick={() => navigate('/')}
            className="text-2xl font-bold tracking-tight cursor-pointer text-white flex items-center gap-0"
            data-testid="logo"
          >
            <img src="/logo.png" alt="NeoMaestro" className="h-9 w-9 object-contain bg-white rounded-lg" />
            <span>NeoMaestro</span>
          </div>
          <Button
            variant="ghost"
            onClick={() => navigate('/')}
            className="text-white/90 hover:text-white"
            data-testid="nav-home-btn"
          >
            Home
          </Button>
        </div>
      </nav>

      <div className="pt-24 px-6 pb-12">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-12"
          >
            <h1 className="text-5xl md:text-6xl font-black tracking-tighter mb-4 text-gray-900" data-testid="marketplace-title">Explore Masterclasses</h1>
            <p className="text-xl text-gray-600">Discover your next learning adventure</p>
          </motion.div>

          {/* Search & Filters */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-gray-50 border border-gray-200 rounded-lg p-6 mb-12"
          >
            <div className="flex flex-col md:flex-row gap-4">
              <div className="flex-1 flex gap-2">
                <Input
                  placeholder="Search with AI (e.g., 'Python for beginners')..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleAISearch()}
                  className="bg-white border-gray-300 text-gray-900 placeholder:text-gray-500 focus:border-[#00a3af]"
                  data-testid="search-input"
                />
                <Button
                  onClick={handleAISearch}
                  className="bg-[#34c759] text-black hover:bg-[#28a745] hover:text-white rounded-lg"
                  data-testid="search-btn"
                >
                  <MagnifyingGlass size={20} weight="bold" />
                </Button>
              </div>
              
              <div className="flex gap-4">
                <Select value={selectedCategory} onValueChange={setSelectedCategory}>
                  <SelectTrigger className="w-[180px] bg-white border-gray-300 text-gray-900" data-testid="category-filter">
                    <Funnel size={16} className="mr-2" />
                    <SelectValue placeholder="Category" />
                  </SelectTrigger>
                  <SelectContent className="bg-white border-gray-200 text-gray-900">
                    <SelectItem value="all">All Categories</SelectItem>
                    {categories.map(cat => (
                      <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                <Select value={selectedFormat} onValueChange={setSelectedFormat}>
                  <SelectTrigger className="w-[150px] bg-white border-gray-300 text-gray-900" data-testid="format-filter">
                    <SelectValue placeholder="Format" />
                  </SelectTrigger>
                  <SelectContent className="bg-white border-gray-200 text-gray-900">
                    <SelectItem value="all">All Formats</SelectItem>
                    <SelectItem value="online">Online</SelectItem>
                    <SelectItem value="offline">Offline</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </motion.div>

          {/* Results */}
          {loading ? (
            <div className="text-center py-12 text-gray-500">Loading masterclasses...</div>
          ) : filteredClasses.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-2xl text-gray-500" data-testid="no-results">No masterclasses found</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {filteredClasses.map((mc, index) => (
                <motion.div
                  key={mc.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                  onClick={() => navigate(`/masterclass/${mc.id}`)}
                  className="bg-white border border-gray-200 rounded-lg overflow-hidden cursor-pointer hover:-translate-y-2 hover:border-[#00a3af] hover:shadow-lg transition-all duration-300"
                  data-testid={`masterclass-card-${index}`}
                >
                  <div className="h-48 bg-gradient-to-br from-[#A3DC00]/10 to-[#00B8CC]/10 flex items-center justify-center">
                    {mc.image_url ? (
                      <img src={mc.image_url} alt={mc.title} className="w-full h-full object-cover" />
                    ) : (
                      <div className="text-6xl opacity-20">🎓</div>
                    )}
                  </div>
                  <div className="p-6">
                    <div className="text-xs uppercase tracking-wider text-[#00a3af] font-bold mb-2">{mc.category}</div>
                    <h3 className="text-xl font-bold mb-2 line-clamp-2 text-gray-900">{mc.title}</h3>
                    <p className="text-sm text-gray-600 mb-4">{mc.instructor_name}</p>
                    <div className="flex justify-between items-center border-t border-gray-200 pt-4">
                      <div>
                        <div className="text-[#00a3af] font-bold text-lg">{mc.price}</div>
                        <div className="text-xs text-gray-500">{mc.format}</div>
                      </div>
                      <div className="text-xs text-gray-500">{mc.date}</div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}